/**
 * Ingredients API Client
 * Frontend API calls for fetching ingredients (for recipe ingredient linking)
 */

import { apiCall } from '../api';
import type {
  Ingredient,
  IngredientFilters,
  IngredientsResponse,
  CreateIngredientRequest,
  IngredientResponse,
  UnitSuggestionResponse,
} from '@/types/api';

interface UpdateIngredientResponse {
  ingredient: Ingredient;
  recipesUpdated?: number;
}

/**
 * Fetch ingredients with optional filtering and pagination
 */
export async function fetchIngredients(
  filters: IngredientFilters = {}
): Promise<IngredientsResponse> {
  const params = new URLSearchParams();

  if (filters.page) params.set('page', filters.page.toString());
  if (filters.pageSize) params.set('pageSize', filters.pageSize.toString());
  if (filters.search) params.set('search', filters.search);
  if (filters.category) params.set('category', filters.category);
  if (filters.activeOnly !== undefined) params.set('activeOnly', String(filters.activeOnly));
  if (filters.sortBy) params.set('sortBy', filters.sortBy);
  if (filters.includeVendorInfo) params.set('includeVendorInfo', 'true');
  if (filters.stapleOnly) params.set('stapleOnly', 'true');

  const queryString = params.toString();
  const url = queryString ? `/api/ingredients?${queryString}` : '/api/ingredients';

  return apiCall<IngredientsResponse>(url, {
    method: 'GET',
  });
}

/**
 * Fetch a single ingredient by ID
 */
export async function fetchIngredient(
  id: number,
  options?: { includeRecipeUsages?: boolean }
): Promise<Ingredient> {
  const params = new URLSearchParams();
  if (options?.includeRecipeUsages) params.set('includeRecipeUsages', 'true');
  const queryString = params.toString();
  const url = queryString ? `/api/ingredients/${id}?${queryString}` : `/api/ingredients/${id}`;

  const response = await apiCall<{ ingredient: Ingredient }>(url, {
    method: 'GET',
  });
  return response.ingredient;
}

/**
 * Create a new ingredient
 * Used by quick-add feature in recipe ingredient section
 */
export async function createIngredient(data: CreateIngredientRequest): Promise<Ingredient> {
  const response = await apiCall<IngredientResponse>('/api/ingredients', {
    method: 'POST',
    body: JSON.stringify(data),
  });
  return response.ingredient;
}

/**
 * Update an existing ingredient
 * Returns the updated ingredient and optionally the number of recipes updated
 * when a unit change cascades to recipe ingredients.
 */
export async function updateIngredient(
  id: number,
  data: Partial<CreateIngredientRequest> & { isActive?: boolean; unitConversionMultiplier?: number }
): Promise<Ingredient & { recipesUpdated?: number }> {
  const response = await apiCall<UpdateIngredientResponse>(`/api/ingredients/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
  const result: Ingredient & { recipesUpdated?: number } = { ...response.ingredient };
  if (response.recipesUpdated !== undefined) {
    result.recipesUpdated = response.recipesUpdated;
  }
  return result;
}

/**
 * Soft-delete an ingredient (deactivate)
 */
export async function deleteIngredient(id: number): Promise<void> {
  await apiCall<{ message: string }>(`/api/ingredients/${id}`, {
    method: 'DELETE',
  });
}

/**
 * The unit an ingredient is normally used in, by name (for a new ingredient) or
 * by id (for an existing one). Read-only.
 */
export async function fetchUnitSuggestion(
  query: { name: string } | { ingredientId: number }
): Promise<UnitSuggestionResponse> {
  const params = new URLSearchParams();
  if ('name' in query) params.set('name', query.name);
  else params.set('ingredientId', String(query.ingredientId));
  return apiCall<UnitSuggestionResponse>(`/api/ingredients/unit-suggestion?${params.toString()}`, { method: 'GET' });
}
